import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

export const workTypeEnum = z.enum(["ONSITE", "REMOTE", "HYBRID"]);

export const jobSourceTypeEnum = z.enum(["API", "SCRAPE"]);
export const jobProviderEnum = z.enum(["adzuna", "jooble", "jsearch", "scrape"]);

export const attributionSchema = z.object({
  source: z.string(),
  sourceUrl: z.string().url(),
  text: z.string(),
});

export const jobListingSchema = z.object({
  id: z.string(),
  externalId: z.string().optional(),
  provider: jobProviderEnum,
  sourceType: jobSourceTypeEnum,

  title: z.string(),
  company: z.string(),
  companyLogo: z.string().url().nullable().optional(),
  location: z.string(),
  countryCode: z.string().length(2),
  workType: workTypeEnum.nullable().optional(),

  description: z.string(),
  descriptionSnippet: z.string().max(200),

  salaryText: z.string().nullable().optional(),
  salaryMin: z.number().nullable().optional(),
  salaryMax: z.number().nullable().optional(),
  salaryCurrency: z.string().nullable().optional(),

  postedAt: z.string().datetime().nullable().optional(),
  applyUrl: z.string().url(),

  attribution: attributionSchema,
});

export type JobListing = z.infer<typeof jobListingSchema>;
export type Attribution = z.infer<typeof attributionSchema>;
export type WorkTypeValue = z.infer<typeof workTypeEnum>;

export const jobSearchQuerySchema = z.object({
  role: z.string().min(1).max(100),
  countryCode: z
    .string()
    .length(2)
    .transform((v) => v.toLowerCase()),
  location: z.string().max(100).optional(),
  workType: workTypeEnum.optional(),
  salaryMin: z.coerce.number().int().nonnegative().optional(),
  page: z.coerce.number().int().min(1).max(50).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20),
});

export class JobSearchQueryDto extends createZodDto(jobSearchQuerySchema) {}
export type JobSearchQuery = z.infer<typeof jobSearchQuerySchema>;

export const jobSearchResultSchema = z.object({
  listings: z.array(jobListingSchema),
  totalCount: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  providers: z.array(jobProviderEnum),
  attributions: z.array(attributionSchema),
  fromCache: z.boolean().default(false),
});

export type JobSearchResult = z.infer<typeof jobSearchResultSchema>;

export const jobMarketDataSchema = z.object({
  salaryRange: z
    .object({
      min: z.number().optional(),
      max: z.number().optional(),
      currency: z.string().optional(),
      median: z.number().optional(),
    })
    .optional(),
  topSkills: z.array(z.string()).max(20).optional(),
  topEmployers: z.array(z.string()).max(20).optional(),
  marketSummary: z.string().optional(),
});

export type JobMarketData = z.infer<typeof jobMarketDataSchema>;

export const jobCategoryHubDtoSchema = z.object({
  id: z.string(),
  countryCode: z.string().length(2),
  roleSlug: z.string(),
  roleName: z.string(),
  marketData: jobMarketDataSchema.nullable().optional(),
  refreshedAt: z.string().datetime(),
});

export type JobCategoryHubDto = z.infer<typeof jobCategoryHubDtoSchema>;
